import { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import "./RentalReservation.css";

export default function RentalReservation({ post, onClose, onSuccess }) {
  const { user } = useSelector((state) => ({ ...state }));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    period: "daily",
    pin: "",
    deliveryRequested: false
  });

  const [calculation, setCalculation] = useState({
    days: 0,
    rentalAmount: 0,
    depositAmount: 0,
    deliveryFee: 0,
    total: 0
  });

  // Calcular valores quando as datas ou período mudarem
  const calculateTotal = () => {
    if (!formData.startDate || !formData.endDate) return;

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));

    if (days <= 0) {
      setError("Data de término deve ser posterior à data de início");
      return;
    }

    let rentalAmount = 0;
    const { rentalData } = post;

    if (formData.period === "daily") {
      rentalAmount = (rentalData.pricePerDay || 0) * days;
    } else if (formData.period === "weekly") {
      const weeks = Math.ceil(days / 7);
      rentalAmount = (rentalData.pricePerWeek || 0) * weeks;
    } else if (formData.period === "monthly") {
      const months = Math.ceil(days / 30);
      rentalAmount = (rentalData.pricePerMonth || 0) * months;
    }

    const depositAmount = rentalData.deposit || 0;
    const deliveryFee = (formData.deliveryRequested && rentalData.deliveryAvailable) 
      ? (rentalData.deliveryFee || 0) 
      : 0;

    setCalculation({
      days,
      rentalAmount,
      depositAmount,
      deliveryFee,
      total: rentalAmount + depositAmount + deliveryFee
    });
    setError("");
  };

  const handleDateChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTimeout(calculateTotal, 100);
  };

  const handlePeriodChange = (period) => {
    setFormData(prev => ({ ...prev, period }));
    setTimeout(calculateTotal, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.startDate || !formData.endDate) {
      setError("Selecione as datas de início e término");
      return;
    }

    if (!formData.pin || formData.pin.length !== 6) {
      setError("PIN da wallet deve ter 6 dígitos");
      return;
    }

    if (calculation.total <= 0) {
      setError("Valor total inválido");
      return;
    }

    setLoading(true);

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/rental/${post._id}/reserve`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${user.token}`
          }
        }
      );

      if (onSuccess) {
        onSuccess(data);
      }
      
      alert("Reserva realizada com sucesso! Pagamento processado via Wallet.");
      onClose();
    } catch (err) {
      console.error("Erro ao criar reserva:", err);
      setError(err.response?.data?.message || "Erro ao processar reserva");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rental_reservation_overlay" onClick={onClose}>
      <div className="rental_reservation_modal" onClick={(e) => e.stopPropagation()}>
        <button className="rental_reservation_close" onClick={onClose}>✕</button>
        
        <h2>Reservar Aluguel</h2>
        <p className="rental_reservation_item">{post.rentalData.itemName}</p>

        <form onSubmit={handleSubmit}>
          {/* Período */}
          <div className="rental_reservation_section">
            <label>Tipo de Período</label>
            <div className="rental_period_options">
              {post.rentalData.pricePerDay && (
                <button
                  type="button"
                  className={`rental_period_btn ${formData.period === "daily" ? "active" : ""}`}
                  onClick={() => handlePeriodChange("daily")}
                >
                  Diário<br />
                  <span>R$ {post.rentalData.pricePerDay.toFixed(2)}/dia</span>
                </button>
              )}
              {post.rentalData.pricePerWeek && (
                <button
                  type="button"
                  className={`rental_period_btn ${formData.period === "weekly" ? "active" : ""}`}
                  onClick={() => handlePeriodChange("weekly")}
                >
                  Semanal<br />
                  <span>R$ {post.rentalData.pricePerWeek.toFixed(2)}/semana</span>
                </button>
              )}
              {post.rentalData.pricePerMonth && (
                <button
                  type="button"
                  className={`rental_period_btn ${formData.period === "monthly" ? "active" : ""}`}
                  onClick={() => handlePeriodChange("monthly")}
                >
                  Mensal<br />
                  <span>R$ {post.rentalData.pricePerMonth.toFixed(2)}/mês</span>
                </button>
              )}
            </div>
          </div>

          {/* Datas */}
          <div className="rental_reservation_dates">
            <div className="rental_reservation_field">
              <label>Data de Início</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleDateChange("startDate", e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                required
              />
            </div>
            <div className="rental_reservation_field">
              <label>Data de Término</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleDateChange("endDate", e.target.value)}
                min={formData.startDate || new Date().toISOString().split("T")[0]}
                required
              />
            </div>
          </div>

          {/* Entrega */}
          {post.rentalData.deliveryAvailable && (
            <div className="rental_reservation_field">
              <label className="rental_checkbox">
                <input
                  type="checkbox"
                  checked={formData.deliveryRequested}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, deliveryRequested: e.target.checked }));
                    setTimeout(calculateTotal, 100);
                  }}
                />
                <span>Solicitar entrega (+ R$ {post.rentalData.deliveryFee?.toFixed(2) || "0.00"})</span>
              </label>
            </div>
          )}

          {/* Cálculo */}
          {calculation.total > 0 && (
            <div className="rental_reservation_calculation">
              <div className="rental_calc_row">
                <span>Período:</span>
                <span>{calculation.days} {calculation.days === 1 ? "dia" : "dias"}</span>
              </div>
              <div className="rental_calc_row">
                <span>Valor do aluguel:</span>
                <span>R$ {calculation.rentalAmount.toFixed(2)}</span>
              </div>
              {calculation.depositAmount > 0 && (
                <div className="rental_calc_row">
                  <span>Caução (será devolvida):</span>
                  <span>R$ {calculation.depositAmount.toFixed(2)}</span>
                </div>
              )}
              {calculation.deliveryFee > 0 && (
                <div className="rental_calc_row">
                  <span>Taxa de entrega:</span>
                  <span>R$ {calculation.deliveryFee.toFixed(2)}</span>
                </div>
              )}
              <div className="rental_calc_row rental_calc_total">
                <span>Total:</span>
                <span>R$ {calculation.total.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* PIN da Wallet */}
          <div className="rental_reservation_field">
            <label>PIN da Wallet (6 dígitos)</label>
            <input
              type="password"
              inputMode="numeric"
              maxLength="6"
              value={formData.pin}
              onChange={(e) => setFormData(prev => ({ ...prev, pin: e.target.value.replace(/\D/g, "") }))}
              placeholder="000000"
              required
            />
            <small className="rental_hint">
              💳 Pagamento exclusivo via Wallet do Socializa
            </small>
          </div>

          {error && <div className="rental_reservation_error">{error}</div>}

          <div className="rental_reservation_actions">
            <button type="button" onClick={onClose} className="rental_btn_cancel">
              Cancelar
            </button>
            <button type="submit" disabled={loading || calculation.total === 0} className="rental_btn_confirm">
              {loading ? "Processando..." : `Confirmar Reserva (R$ ${calculation.total.toFixed(2)})`}
            </button>
          </div>
        </form>

        <div className="rental_reservation_terms">
          <p>⚠️ Ao confirmar, você concorda com os termos do aluguel:</p>
          {post.rentalData.rentalTerms && (
            <p className="rental_terms_text">{post.rentalData.rentalTerms}</p>
          )}
          <p>• O pagamento será debitado imediatamente da sua Wallet</p>
          <p>• A caução será bloqueada e devolvida ao fim do período</p>
          <p>• Cancelamentos seguem a política do anunciante</p>
        </div>
      </div>
    </div>
  );
}
